import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
    AlertTriangle,
    Clock,
    FileText,
    Banknote,
    Receipt,
    CheckSquare,
    RefreshCcw,
    Gauge,
    CalendarClock,
    ArrowUpRight,
    ChevronDown,
    ChevronRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const BUCKET_META = [
    {
        key: "overdue_cards",
        title: "Overdue",
        subtitle: "Payment past due date and unpaid",
        icon: AlertTriangle,
        tone: "red",
        ctaTo: "/payments",
        ctaLabel: "Record payment",
    },
    {
        key: "payments_due",
        title: "Due in ≤ 7 days",
        subtitle: "Pay before it becomes overdue",
        icon: Clock,
        tone: "amber",
        ctaTo: "/payments",
        ctaLabel: "Record payment",
    },
    {
        key: "missing_statements",
        title: "Missing statements",
        subtitle: "Statement date passed, PDF not uploaded",
        icon: FileText,
        tone: "amber",
        ctaTo: "/statements",
        ctaLabel: "Upload statement",
    },
    {
        key: "payments_not_reflected",
        title: "Payments pending reflection",
        subtitle: "Recorded but not yet posted on card",
        icon: Banknote,
        tone: "amber",
        ctaTo: "/payments",
        ctaLabel: "Verify posting",
    },
    {
        key: "unapproved_expenses",
        title: "Unapproved expenses",
        subtitle: "Waiting on approval",
        icon: CheckSquare,
        tone: "blue",
        ctaTo: "/expenses",
        ctaLabel: "Review",
    },
    {
        key: "missing_receipts",
        title: "Missing receipts",
        subtitle: "Expense entries without a receipt file",
        icon: Receipt,
        tone: "stone",
        ctaTo: "/expenses",
        ctaLabel: "Attach receipt",
    },
    {
        key: "unreconciled_statements",
        title: "Unreconciled statements",
        subtitle: "Upload matches with entries pending",
        icon: RefreshCcw,
        tone: "blue",
        ctaTo: "/reconciliation",
        ctaLabel: "Reconcile",
    },
    {
        key: "high_utilization",
        title: "High utilization (>70%)",
        subtitle: "Consider paying down or spreading spend",
        icon: Gauge,
        tone: "red",
        ctaTo: "/cards",
        ctaLabel: "View cards",
    },
    {
        key: "expiring_cards",
        title: "Cards expiring ≤ 90 days",
        subtitle: "Plan renewals / replacements",
        icon: CalendarClock,
        tone: "stone",
        ctaTo: "/cards",
        ctaLabel: "Open card",
    },
];

const TONE = {
    red: {
        chip: "bg-red-50 text-red-700 border-red-200",
        dot: "bg-red-500",
        text: "text-red-700",
        border: "border-red-200",
    },
    amber: {
        chip: "bg-amber-50 text-amber-700 border-amber-200",
        dot: "bg-amber-500",
        text: "text-amber-700",
        border: "border-amber-200",
    },
    blue: {
        chip: "bg-blue-50 text-blue-700 border-blue-200",
        dot: "bg-blue-500",
        text: "text-blue-700",
        border: "border-blue-200",
    },
    stone: {
        chip: "bg-stone-50 text-stone-700 border-stone-200",
        dot: "bg-stone-500",
        text: "text-stone-700",
        border: "border-stone-200",
    },
};

function BucketRow({ meta, item }) {
    const tone = TONE[meta.tone] || TONE.stone;
    // Row-level quick-action deep link — pre-fills the target module so the
    // user lands directly on the row's card/statement instead of a generic
    // list. Only rendered where we have a stable identifier to route on.
    const quickAction = (() => {
        if ((meta.key === "payments_due" || meta.key === "overdue_cards") && item.card_id) {
            return { to: `/payments?card=${item.card_id}`, label: "Record payment" };
        }
        if (meta.key === "missing_statements" && item.card_id) {
            return { to: `/statements?card=${item.card_id}`, label: "Upload statement" };
        }
        if (meta.key === "high_utilization" && item.card_id) {
            return { to: `/payments?card=${item.card_id}`, label: "Pay down" };
        }
        if (meta.key === "expiring_cards" && item.card_id) {
            return { to: `/cards/${item.card_id}`, label: "Renew" };
        }
        return null;
    })();

    const QuickActionBtn = quickAction ? (
        <Link
            to={quickAction.to}
            onClick={(e) => e.stopPropagation()}
            className="ml-2 text-[11px] px-2.5 py-1 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors shrink-0 inline-flex items-center gap-1"
            data-testid={`fr-quick-action-${meta.key}-${item.card_id || item.id || ""}`}
        >
            {quickAction.label} <ArrowUpRight size={10} />
        </Link>
    ) : null;

    if (meta.key === "payments_due" || meta.key === "overdue_cards") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} shrink-0`} />
                    <span className="truncate">{item.label}</span>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right tabular-nums">
                        <div className="font-medium">{fmtMoney(item.outstanding)}</div>
                        <div className="text-[11px] text-stone-500">
                            {item.due_date ? `Due ${fmtDate(item.due_date)}` : "—"}
                            {item.days_left != null && (
                                <span className={`ml-2 ${item.days_left < 0 ? "text-red-700" : ""}`}>
                                    {item.days_left < 0
                                        ? `${Math.abs(item.days_left)}d overdue`
                                        : item.days_left === 0
                                          ? "today"
                                          : `${item.days_left}d left`}
                                </span>
                            )}
                        </div>
                    </div>
                    {QuickActionBtn}
                </div>
            </div>
        );
    }
    if (meta.key === "missing_statements") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="truncate">{item.label}</div>
                <div className="flex items-center gap-2 shrink-0">
                    <div className="text-right text-[11px] text-stone-500 tabular-nums">
                        Expected {fmtDate(item.expected_date)}
                        {item.days_overdue > 0 && (
                            <span className="text-amber-700 ml-2">· {item.days_overdue}d late</span>
                        )}
                    </div>
                    {QuickActionBtn}
                </div>
            </div>
        );
    }
    if (meta.key === "payments_not_reflected") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="min-w-0">
                    <div className="truncate">{item.label}</div>
                    <div className="text-[11px] text-stone-500">
                        {fmtDate(item.date)}
                        {item.utr && (
                            <span className="ml-2 font-mono-figure">UTR {item.utr}</span>
                        )}
                    </div>
                </div>
                <div className="text-right tabular-nums font-medium shrink-0">{fmtMoney(item.amount)}</div>
            </div>
        );
    }
    if (meta.key === "unapproved_expenses" || meta.key === "missing_receipts") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="min-w-0">
                    <div className="truncate">{item.merchant || "—"}</div>
                    <div className="text-[11px] text-stone-500">
                        {fmtDate(item.date)} · •••• {item.last_4 || "----"}
                        {item.entered_by_name && ` · by ${item.entered_by_name}`}
                    </div>
                </div>
                <div className="text-right tabular-nums font-medium shrink-0">{fmtMoney(item.amount)}</div>
            </div>
        );
    }
    if (meta.key === "unreconciled_statements") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="min-w-0">
                    <div className="truncate">{item.label}</div>
                    <div className="text-[11px] text-stone-500">
                        {item.statement_month} · {item.status}
                    </div>
                </div>
                <div className="text-right tabular-nums font-medium shrink-0">{fmtMoney(item.total_due)}</div>
            </div>
        );
    }
    if (meta.key === "high_utilization") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="truncate">{item.label}</div>
                <div className="text-right shrink-0 tabular-nums">
                    <div className="font-medium">{item.utilization_pct}%</div>
                    <div className="text-[11px] text-stone-500">
                        {fmtMoney(item.outstanding)} / {fmtMoney(item.credit_limit)}
                    </div>
                </div>
            </div>
        );
    }
    if (meta.key === "expiring_cards") {
        return (
            <div className="flex items-center justify-between gap-3 py-2.5 border-b border-stone-100 last:border-0 text-sm">
                <div className="min-w-0">
                    <div className="truncate">{item.label}</div>
                    <div className="text-[11px] text-stone-500">
                        Expires {fmtDate(item.expires_on)}
                    </div>
                </div>
                <div className={`text-right shrink-0 text-xs font-medium ${tone.text}`}>
                    {item.days_left}d · {item.bucket}
                </div>
            </div>
        );
    }
    return null;
}

function Bucket({ meta, items, onApprove }) {
    const [open, setOpen] = useState(items.length > 0 && items.length <= 5);
    const tone = TONE[meta.tone] || TONE.stone;
    const count = items.length;
    if (count === 0) {
        return (
            <div className="rounded-lg border border-stone-200 bg-white p-4 opacity-60">
                <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-md border ${tone.border} bg-white flex items-center justify-center`}>
                        <meta.icon size={14} className={tone.text} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-stone-700">{meta.title}</div>
                        <div className="text-[11px] text-stone-500">All clear</div>
                    </div>
                    <span className="text-xs text-emerald-700 font-medium">0</span>
                </div>
            </div>
        );
    }
    return (
        <div
            className={`rounded-lg border ${tone.border} bg-white overflow-hidden`}
            data-testid={`fr-bucket-${meta.key}`}
        >
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-3 p-4 hover:bg-stone-50 transition-colors text-left"
                data-testid={`fr-bucket-toggle-${meta.key}`}
            >
                <div className={`w-8 h-8 rounded-md ${tone.chip} border flex items-center justify-center`}>
                    <meta.icon size={14} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-900">
                        {meta.title}
                        <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${tone.chip}`}>
                            {count}
                        </span>
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5">{meta.subtitle}</div>
                </div>
                <Link
                    to={meta.ctaTo}
                    onClick={(e) => e.stopPropagation()}
                    className="hidden sm:inline-flex items-center gap-1 text-[11px] text-stone-700 hover:text-stone-900 border border-stone-200 rounded-full px-2.5 py-1 hover:border-stone-900 transition-colors"
                    data-testid={`fr-cta-${meta.key}`}
                >
                    {meta.ctaLabel} <ArrowUpRight size={11} />
                </Link>
                {open ? (
                    <ChevronDown size={14} className="text-stone-400" />
                ) : (
                    <ChevronRight size={14} className="text-stone-400" />
                )}
            </button>
            {open && (
                <div className="px-4 pb-3 border-t border-stone-100">
                    {items.slice(0, 25).map((item, i) => {
                        const rowKey = item.id || item.card_id || `${meta.key}-${i}`;
                        return (
                            <div key={rowKey} className="flex items-center gap-2">
                                <div className="flex-1 min-w-0">
                                    <BucketRow meta={meta} item={item} />
                                </div>
                                {meta.key === "unapproved_expenses" && item.id && (
                                    <button
                                        onClick={() => onApprove(item.id)}
                                        className="ml-2 text-[11px] px-2.5 py-1 rounded-full border border-emerald-200 text-emerald-700 hover:bg-emerald-50 transition-colors shrink-0"
                                        data-testid={`fr-approve-${item.id}`}
                                    >
                                        Approve
                                    </button>
                                )}
                            </div>
                        );
                    })}
                    {items.length > 25 && (
                        <div className="text-[11px] text-stone-500 pt-2">
                            Showing 25 of {items.length}. Open the full module to see the rest.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const SAVED_VIEWS = [
    { key: "all",                     label: "All buckets" },
    { key: "overdue_cards",           label: "Overdue" },
    { key: "payments_due",            label: "Due this week" },
    { key: "missing_statements",      label: "Missing statements" },
    { key: "payments_not_reflected",  label: "Payment not reflected" },
    { key: "missing_receipts",        label: "Missing receipts" },
    { key: "unapproved_expenses",     label: "Pending approval" },
    { key: "high_utilization",        label: "High utilization" },
    { key: "expiring_cards",          label: "Cards expiring" },
];

function ScopedExpenseUser({ data }) {
    return (
        <div className="space-y-4">
            <BucketList data={data} keys={["unapproved_expenses", "missing_receipts", "reimbursable_pending"]} labels={{
                unapproved_expenses: "Waiting on approval",
                missing_receipts: "Attach a receipt",
                reimbursable_pending: "Pending reimbursement",
            }} />
        </div>
    );
}

function BucketList({ data, keys, labels }) {
    return (
        <>
            {keys.map((k) => {
                const items = data[k] || [];
                return (
                    <div key={k} className="rounded-lg border border-stone-200 bg-white p-4">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="text-sm font-medium text-stone-900">{labels[k]}</div>
                            <span className="text-[11px] px-2 py-0.5 rounded-full border border-stone-200">
                                {items.length}
                            </span>
                        </div>
                        {items.length === 0 && <div className="text-xs text-stone-500">Nothing here — you&apos;re clear.</div>}
                        {items.map((e) => (
                            <div
                                key={e.id}
                                className="flex items-center justify-between py-2 border-t border-stone-100 text-sm"
                            >
                                <div>
                                    <div className="truncate">{e.merchant || "—"}</div>
                                    <div className="text-[11px] text-stone-500">{fmtDate(e.date)}</div>
                                </div>
                                <div className="tabular-nums font-medium">{fmtMoney(e.amount)}</div>
                            </div>
                        ))}
                    </div>
                );
            })}
        </>
    );
}

export default function FinanceReview() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState("all");

    const load = useCallback(() => {
        setLoading(true);
        return api
            .get("/finance-review")
            .then((r) => setData(r.data))
            .catch((e) => {
                console.error("[FinanceReview] load failed", e);
                toast.error("Could not load review — please retry");
            })
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const onApprove = useCallback(
        async (id) => {
            try {
                await api.post(`/finance-review/expenses/${id}/approve`);
                toast.success("Expense approved");
                load();
            } catch (e) {
                toast.error("Approval failed");
                console.error("[FinanceReview] approve failed", e);
            }
        },
        [load],
    );

    const totalItems = useMemo(() => data?.counts?.total_action_items ?? 0, [data]);

    if (user?.role === "expense_user") {
        if (loading || !data) return <div className="text-stone-500 text-sm">Loading your review…</div>;
        return (
            <div data-testid="finance-review-page">
                <div className="mb-8">
                    <div className="eyebrow">Your review</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Your action items.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3">Things pending on the expenses you posted.</p>
                </div>
                <ScopedExpenseUser data={data} />
            </div>
        );
    }

    return (
        <div data-testid="finance-review-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                <div>
                    <div className="eyebrow">Finance Action Center</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        What needs your attention today.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-xl">
                        One page for the daily finance stand-up — every open item across cards, payments,
                        statements and reconciliation.
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <div
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-full border border-stone-200 bg-white text-sm"
                        data-testid="fr-total-count"
                    >
                        <span className={`w-2 h-2 rounded-full ${totalItems > 0 ? "bg-red-500" : "bg-emerald-500"}`} />
                        <span className="font-medium">{totalItems}</span>
                        <span className="text-stone-500">open items</span>
                    </div>
                    <Button
                        onClick={load}
                        variant="outline"
                        size="sm"
                        disabled={loading}
                        data-testid="fr-refresh"
                    >
                        <RefreshCcw size={13} className="mr-1.5" /> Refresh
                    </Button>
                </div>
            </div>

            {loading && !data && (
                <div className="text-sm text-stone-500">Building your action list…</div>
            )}

            {data && (
                <>
                    <div className="flex flex-wrap gap-2 mb-5" data-testid="fr-saved-views">
                        {SAVED_VIEWS.map((v) => {
                            const active = view === v.key;
                            const count = v.key === "all"
                                ? totalItems
                                : (data[v.key] || []).length;
                            return (
                                <button
                                    key={v.key}
                                    type="button"
                                    onClick={() => setView(v.key)}
                                    className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                                        active
                                            ? "bg-stone-900 border-stone-900 text-white"
                                            : "bg-white border-stone-200 text-stone-700 hover:border-stone-400"
                                    }`}
                                    data-testid={`fr-view-${v.key}`}
                                >
                                    {v.label}
                                    <span className={`text-[10px] tabular-nums px-1.5 py-0.5 rounded-full ${active ? "bg-white/20" : "bg-stone-100 text-stone-600"}`}>
                                        {count}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <div className={view === "all" ? "grid grid-cols-1 lg:grid-cols-2 gap-4" : "grid grid-cols-1 gap-4"}>
                        {BUCKET_META
                            .filter((meta) => view === "all" || meta.key === view)
                            .map((meta) => (
                                <Bucket
                                    key={meta.key}
                                    meta={meta}
                                    items={data[meta.key] || []}
                                    onApprove={onApprove}
                                />
                            ))}
                    </div>
                </>
            )}

            {data && totalItems === 0 && (
                <div className="mt-8 rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
                    <div className="font-display text-2xl text-emerald-800">You&rsquo;re fully caught up.</div>
                    <div className="text-sm text-emerald-700 mt-2">
                        Nothing overdue, no missing statements, no pending approvals. Beautiful.
                    </div>
                </div>
            )}
        </div>
    );
}
